/**
 * 動画生成の失敗理由コード（バックエンドの App\Support\VideoFailureCode）を
 * 画面の文言へ落とす。
 *
 * API が返す `error_message` は利用者向けの1文だが、技術情報を含むものは
 * 「動画の生成に失敗しました。再試行してください」へ丸められてしまうため、
 * 「参照画像を外せば通るのか」「モデルを変えるべきか」は読み取れない。
 * 判定はバックエンドの `error_code` だけを見る（文言判定を画面に書かない）。
 */

export type VideoFailureCode =
  | 'sensitive_reference_image'
  | 'input_rejected'
  | 'reference_image_spec'
  | 'aspect_ratio'
  | 'model_unavailable'
  | 'unsupported_duration'
  | 'provider_timeout'
  | 'provider_balance'
  | 'provider_blocked'
  | 'cancelled'
  | 'unknown';

/** 一覧カードのバッジに出す短いラベル。 */
export const VIDEO_FAILURE_LABELS: Record<VideoFailureCode, string> = {
  sensitive_reference_image: '人物が写る参照画像',
  input_rejected: '入力が受け付けられませんでした',
  reference_image_spec: '参照画像の規格外',
  aspect_ratio: 'アスペクト比が非対応',
  model_unavailable: 'モデルが利用不可',
  unsupported_duration: '秒数が非対応',
  provider_timeout: 'タイムアウト',
  provider_balance: '提供元の残高不足',
  provider_blocked: '提供元で一時ブロック',
  cancelled: 'キャンセル',
  unknown: '失敗',
};

/** 詳細モーダルに出す説明。`error_message` が空のジョブでもここだけで案内できる。 */
const VIDEO_FAILURE_DESCRIPTIONS: Record<VideoFailureCode, string> = {
  sensitive_reference_image:
    '参照画像に実在の人物が写っていると判定されたため生成できませんでした。参照画像を外すか、人物の参照画像に対応したモデルで作り直してください。',
  input_rejected:
    'プロンプトまたは参照画像が受け付けられませんでした。表現を変えるか、参照画像を差し替えて作り直してください。',
  reference_image_spec:
    '参照画像がモデルの条件（PNG/JPEG・幅と高さ300〜6000px・縦横比0.4〜2.5）を満たしていません。別の画像を選ぶか、参照画像なしで作り直してください。',
  aspect_ratio: '選択したアスペクト比はこのモデルで使えません。比率を変えて作り直してください。',
  model_unavailable: '選択したモデルが現在利用できません。別のモデルを選んで作り直してください。',
  unsupported_duration: '選択した秒数はこのモデルが受け付けません。秒数を変えて作り直してください。',
  provider_timeout:
    '生成が時間内に終わりませんでした。混雑している可能性があるので、少し待ってから作り直してください。',
  provider_balance: '生成の提供元で処理できませんでした。運営までお問い合わせください。',
  provider_blocked: '提供元へのアクセスが一時的に制限されています。しばらく待ってから作り直してください。',
  cancelled: 'このジョブはキャンセルされました。',
  unknown: '動画の生成に失敗しました。設定を変えて作り直してください。',
};

const isVideoFailureCode = (code: string | null | undefined): code is VideoFailureCode =>
  !!code && code in VIDEO_FAILURE_LABELS;

/** 一覧カード用。理由が分からない失敗で「失敗 · 失敗」にならないよう undefined を返す。 */
export const videoFailureLabel = (code?: string | null): string | undefined =>
  isVideoFailureCode(code) && code !== 'unknown' ? VIDEO_FAILURE_LABELS[code] : undefined;

/** 詳細モーダル用。未知のコードや未設定でも必ず1文を返す。 */
export const describeVideoFailure = (code?: string | null): string =>
  isVideoFailureCode(code) ? VIDEO_FAILURE_DESCRIPTIONS[code] : VIDEO_FAILURE_DESCRIPTIONS.unknown;
